import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout, Button, Progress, Tag, Modal, message, Input, Spin, Result } from 'antd';
import html2canvas from 'html2canvas';
import { 
  HomeOutlined, 
  ShareAltOutlined, 
  ReloadOutlined, 
  WeiboOutlined, 
  WechatOutlined, 
  QqOutlined, 
  HeartOutlined, 
  CopyOutlined, 
  DownloadOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  StopOutlined 
} from '@ant-design/icons';
import { getZodiacInfo } from '../data/zodiacData';
import './IntroPage.css';
import './ReportPage.css';

const { Content } = Layout;

const ReportPage = () => {
  const navigate = useNavigate();
  const { type } = useParams();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const shareImageRef = useRef(null);
  
  // 根据测试类型获取人称代词
  const getPronouns = () => {
    if (type === 'self') {
      return { subject: '您', object: '伴侣', possessive: '您的', partner: '对方' };
    } else {
      return { subject: 'Ta', object: '您', possessive: 'Ta的', partner: '您' };
    }
  };
  const p = getPronouns();

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleRestart = () => {
    // 清除当前类型的数据
    const sessionId = localStorage.getItem('sessionId');
    localStorage.removeItem(`rpi_answers_${type}_${sessionId}`);
    localStorage.removeItem(`rpi_demographic_${type}_${sessionId}`);
    localStorage.removeItem(`rpi_completed_${type}`);
    navigate(`/consent/${type}`);
  };

  useEffect(() => {
    window.scrollTo({ top: 0 });
    
    // 检查sessionId
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      navigate('/');
      return;
    }

    // 检查是否有指定的报告ID（从"我的测试报告"页面跳转）
    const reportId = localStorage.getItem('currentReportId');
    
    if (reportId) {
      // 从报告列表中查找该报告
      const reports = JSON.parse(localStorage.getItem(`rpi_reports_${type}_${sessionId}`) || '[]');
      const report = reports.find(r => r.id === reportId);
      
      if (report) {
        // 找到报告，重新计算完整的 RPI 数据
        const result = calculateRPI(report.answers);
        setReportData({ 
          ...result, 
          demographic: report.demographic, 
          type, 
          timestamp: report.timestamp 
        });
        return;
      }
    }

    // 没有报告ID，尝试从最新的测试数据生成报告
    const answers = JSON.parse(localStorage.getItem(`rpi_answers_${type}_${sessionId}`) || '[]');
    const demographic = JSON.parse(localStorage.getItem(`rpi_demographic_${type}_${sessionId}`) || '{}');
    
    if (answers.length === 40) {
      const result = calculateRPI(answers);
      setReportData({ ...result, demographic, type, timestamp: new Date().toLocaleString('zh-CN') });
    } else {
      message.error('未找到测试数据');
      navigate('/');
    }
  }, [type, navigate]);

  const calculateRPI = (answers) => {
    // 计算每个维度的原始分和z分数
    const calcDimension = (start, end) => {
      const slice = answers.slice(start, end);
      const raw = slice.reduce((sum, val) => sum + val, 0) / slice.length;
      // 调整后的z分数计算（降低均值，让分数偏高）
      const z = ((raw - 2.2) / 1.0).toFixed(2);
      // 调整百分制转换（让分数更高）
      const percent = Math.round(Math.min(100, (raw / 5) * 100 * 1.2));
      return { raw: raw.toFixed(2), z: parseFloat(z), percent };
    };

    const control = calcDimension(0, 10);
    const jealousy = calcDimension(10, 20);
    const dependency = calcDimension(20, 30);
    const insecurity = calcDimension(30, 40);

    // RPI指数计算（调整算法让分数偏高）
    const avgZ = (control.z + jealousy.z + dependency.z + insecurity.z) / 4;
    // 调整百分位计算，让分数更高
    const rpiIndex = Math.round(Math.min(100, Math.max(0, (avgZ + 2.5) / 5 * 100)));

    // 确定等级（使用粉红色系）
    let level, levelText, levelColor;
    if (rpiIndex < 20) {
      level = '很低'; levelText = '自由自在'; levelColor = '#F9A8D4';
    } else if (rpiIndex < 40) {
      level = '偏低'; levelText = '松弛有度'; levelColor = '#F472B6';
    } else if (rpiIndex < 60) {
      level = '中等'; levelText = '恰到好处'; levelColor = '#EC4899';
    } else if (rpiIndex < 80) {
      level = '偏高'; levelText = '执着深情'; levelColor = '#DB2777';
    } else {
      level = '很高'; levelText = '爱到极致'; levelColor = '#BE185D';
    }

    return {
      rpiIndex,
      level,
      levelText,
      levelColor,
      dimensions: { control, jealousy, dependency, insecurity }
    };
  };

  if (!reportData) {
    return null;
  }

  // 解构数据并提供默认值
  const { rpiIndex, level, levelText, levelColor, dimensions = {} } = reportData;
  
  // 确保所有维度都存在，添加默认值
  const safeDimensions = {
    control: dimensions.control || { raw: '0', z: 0, percent: 0 },
    jealousy: dimensions.jealousy || { raw: '0', z: 0, percent: 0 },
    dependency: dimensions.dependency || { raw: '0', z: 0, percent: 0 },
    insecurity: dimensions.insecurity || { raw: '0', z: 0, percent: 0 }
  };
  
  // 检查是否有有效的维度数据
  const hasValidDimensions = dimensions.control && dimensions.jealousy && 
                             dimensions.dependency && dimensions.insecurity;
  
  if (!hasValidDimensions) {
    console.error('维度数据不完整:', dimensions);
  }

  // 生成分享链接
  const shareUrl = window.location.href;
  
  // 复制链接
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      message.success('链接已复制到剪贴板');
    });
  };

  // 保存为图片
  const handleSaveAsImage = async () => {
    if (!shareImageRef.current) return;
    
    try {
      message.loading({ content: '正在生成图片...', key: 'generating' });
      
      const canvas = await html2canvas(shareImageRef.current, {
        scale: 2, // 提高清晰度
        backgroundColor: '#FFF0F5',
        logging: false,
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `RPI测试报告_${rpiIndex}分_${new Date().getTime()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      message.success({ content: '图片已保存！', key: 'generating' });
    } catch (error) {
      console.error('生成图片失败:', error);
      message.error({ content: '生成图片失败，请重试', key: 'generating' });
    }
  };

  // 复制文案
  const handleCopyText = () => {
    const shareText = `我的恋爱占有欲指数 (RPI) 是 ${rpiIndex} 分，处于「${levelText}」水平！💕

RPI 恋爱占有欲指数测试 - 基于心理学研究的专业测评工具，帮助你更好地了解恋爱关系中的占有欲特征，促进健康和谐的亲密关系！

立即测试：https://xinlitest.vip/

#RPI测试 #恋爱占有欲 #关系心理学`;
    navigator.clipboard.writeText(shareText).then(() => {
      message.success('文案已复制到剪贴板');
    });
  };

  // 检测是否是移动端
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // 社交媒体分享处理
  const handleSocialShare = (platform) => {
    const shareText = `我的恋爱占有欲指数 (RPI) 是 ${rpiIndex} 分，处于「${levelText}」水平！💕 立即测试：https://xinlitest.vip/`;
    const mobile = isMobile();
    let url = '';
    
    switch(platform) {
      case 'weibo':
        if (mobile) {
          // 移动端：尝试打开微博 App
          url = `sinaweibo://qr/userinfo?uid=${encodeURIComponent(shareUrl)}`;
          window.location.href = url;
          // 备用方案：如果App未安装，跳转到网页版
          setTimeout(() => {
            window.location.href = `https://service.weibo.com/share/share.php?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`;
          }, 500);
          return;
        } else {
          url = `https://service.weibo.com/share/share.php?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`;
        }
        break;
      case 'wechat':
        if (mobile) {
          // 移动端：提示用户使用手机自带分享功能
          message.info('请点击右上角"..."按钮，选择"分享到朋友圈"', 3);
        } else {
          message.info('请在手机端打开此页面，使用手机自带分享功能', 3);
        }
        return;
      case 'qq':
        if (mobile) {
          // 移动端：尝试打开 QQ App
          url = `mqqapi://share/to_fri?src_type=web&version=1&file_type=news&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`;
          window.location.href = url;
          // 备用方案
          setTimeout(() => {
            window.location.href = `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`;
          }, 500);
          return;
        } else {
          url = `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`;
        }
        break;
      case 'qzone':
        url = `https://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`;
        break;
      case 'douban':
        url = `https://www.douban.com/share/service?href=${encodeURIComponent(shareUrl)}&name=${encodeURIComponent(shareText)}`;
        break;
      default:
        return;
    }
    
    if (url) {
      if (mobile) {
        window.location.href = url;
      } else {
        window.open(url, '_blank', 'width=600,height=400');
      }
    }
  };

  return (
    <Layout className="rpi-intro-layout">
      {/* Header */}
      <header className="rpi-header">
        <div className="rpi-header-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>💕</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--rpi-primary)' }}>
              RPI 评估结果
            </span>
          </div>
        </div>
      </header>

      <Content>
        <div className="rpi-report-main">
          {/* RPI总指数 */}
          <div className="rpi-report-hero-card">
            <div className="rpi-report-hero-icon-wrapper">
              <div className="rpi-report-hero-icon">💕</div>
            </div>
            <h1 className="rpi-report-hero-title">
              {type === 'self' ? '恋爱占有欲指数 (RPI)' : '恋人占有欲指数 (RPI)'}
            </h1>
            <div className="rpi-report-hero-score">{rpiIndex}</div>
            <div className="rpi-report-hero-level" style={{ background: levelColor, color: 'white' }}>
              {level} （{levelText}）
            </div>
            <p className="rpi-report-hero-text">
              {p.subject}的恋爱占有欲指数为 <strong>{rpiIndex}</strong>，处于「{level}（{levelText}）」水平
            </p>
            
            {/* 进度条 */}
            <div className="rpi-report-scale">
              <div className="rpi-report-scale-bar" style={{ background: 'linear-gradient(to right, #F472B6, #EC4899, #DB2777, #BE185D)' }}>
                <div className="rpi-report-scale-marker" style={{ left: `${rpiIndex}%`, background: '#DC2626' }}></div>
              </div>
              <div className="rpi-report-scale-labels">
                <span>0（自由自在）</span>
                <span>50（恰到好处）</span>
                <span>100（爱到极致）</span>
              </div>
            </div>

            {/* 结果解释 */}
            <div className="rpi-report-interpretation">
              <div className="rpi-interpretation-header">
                <span className="rpi-interpretation-emoji">💭</span>
                <h3>综合结果解释</h3>
              </div>
              
              <div className="rpi-report-interpretation-content">
                {/* 根据不同分数段显示不同内容 */}
                {rpiIndex < 20 && (
                  <>
                    <p className="rpi-interpretation-summary-text">
                      {type === 'self' ? '您' : 'Ta'}的恋爱占有欲指数为 <strong className="rpi-score-highlight">{rpiIndex}</strong> 分，处于「<strong>很低（自由自在）</strong>」水平。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      {type === 'self' 
                        ? '您在恋爱关系中表现出很强的独立性和信任感，给予伴侣充分的自由和空间。您不太会因为伴侣的社交活动而感到不安，也很少有监控或限制对方的行为。'
                        : 'Ta在恋爱关系中表现出很强的独立性和信任感，给予您充分的自由和空间。Ta不太会因为您的社交活动而感到不安，也很少有监控或限制您的行为。'
                      }
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      在以下维度上表现优秀：高度信任{type === 'self' ? '伴侣' : '您'}，尊重{type === 'self' ? '对方' : '您'}的个人空间，情感独立性强。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      这种松弛的相处模式有利于建立健康平等的亲密关系。不过，适度的关注和在乎也能让{type === 'self' ? '对方' : '您'}感受到被重视。
                    </p>
                  </>
                )}
                
                {rpiIndex >= 20 && rpiIndex < 40 && (
                  <>
                    <p className="rpi-interpretation-summary-text">
                      您的恋爱占有欲指数为 <strong className="rpi-score-highlight">{rpiIndex}</strong> 分，处于「<strong>偏低（松弛有度）</strong>」水平。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      您在恋爱中保持着良好的平衡，既重视关系又不会过度控制。您会偶尔想了解伴侣的动态，但总体上给予对方足够的信任和自由。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      在以下维度上表现良好：信任与关注并存，偶尔会有轻微的不安或嫉妒，但能够理性处理。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      这种适度的占有欲有助于维持健康稳定的关系，伴侣既能感受到被爱，又不会感到被束缚。
                    </p>
                  </>
                )}
                
                {rpiIndex >= 40 && rpiIndex < 60 && (
                  <>
                    <p className="rpi-interpretation-summary-text">
                      您的恋爱占有欲指数为 <strong className="rpi-score-highlight">{rpiIndex}</strong> 分，处于「<strong>中等（恰到好处）</strong>」水平。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      您的占有欲处于正常范围内，既不会过度冷漠也不会过分控制。您会关心伴侣的行踪和社交，偶尔会有嫉妒或不安的情绪，但通常能够自我调节。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      在以下维度上表现平衡：在意伴侣但不过度干涉，会因某些情况产生嫉妒但能控制，需要一定的确认和关注但不依赖。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      这是比较常见且健康的恋爱模式。建议继续保持开放的沟通，及时表达自己的感受和需求。
                    </p>
                  </>
                )}
                
                {rpiIndex >= 60 && rpiIndex < 80 && (
                  <>
                    <p className="rpi-interpretation-summary-text">
                      您的恋爱占有欲指数为 <strong className="rpi-score-highlight">{rpiIndex}</strong> 分，处于「<strong>很高（爱到极致）</strong>」水平。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      这表明你在恋爱关系中占有欲较为强烈，建议关注是否对关系造成过度压力。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      在以下维度上得分较高：嫉妒倾向、情感依赖、关系不安。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      这些特点可能影响你在恋爱关系中的行为方式和情感体验。
                    </p>
                  </>
                )}
                
                {rpiIndex >= 80 && (
                  <>
                    <p className="rpi-interpretation-summary-text">
                      您的恋爱占有欲指数为 <strong className="rpi-score-highlight">{rpiIndex}</strong> 分，处于「<strong>很高（爱到极致）</strong>」水平。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      您的占有欲非常强烈，可能表现为频繁查看伴侣动态、限制社交活动等。这种极端的占有欲通常与深层的不安全感有关。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      在以下维度上得分极高：控制欲望、嫉妒倾向、情感依赖、关系不安。
                    </p>
                    
                    <p className="rpi-interpretation-desc">
                      这种程度的占有欲可能已经严重影响了关系质量。建议试着给彼此更多空间，培养独立的兴趣爱好。如果感到情绪难以控制，可以向信任的朋友倾诉。
                    </p>
                  </>
                )}
                
                <div className="rpi-report-interpretation-highlights">
                  <h4>🎯 关键发现</h4>
                  <div className="rpi-report-highlight-grid">
                    {safeDimensions.control.percent >= 60 && (
                      <div className="rpi-report-highlight-item">
                        <span className="rpi-report-highlight-icon">🎯</span>
                        <span><strong>控制欲望较高</strong>（{safeDimensions.control.raw}分）</span>
                      </div>
                    )}
                    {safeDimensions.jealousy.percent >= 60 && (
                      <div className="rpi-report-highlight-item">
                        <span className="rpi-report-highlight-icon">💔</span>
                        <span><strong>嫉妒倾向明显</strong>（{safeDimensions.jealousy.raw}分）</span>
                      </div>
                    )}
                    {safeDimensions.dependency.percent >= 60 && (
                      <div className="rpi-report-highlight-item">
                        <span className="rpi-report-highlight-icon">💞</span>
                        <span><strong>情感依赖度高</strong>（{safeDimensions.dependency.raw}分）</span>
                      </div>
                    )}
                    {safeDimensions.insecurity.percent >= 60 && (
                      <div className="rpi-report-highlight-item">
                        <span className="rpi-report-highlight-icon">😰</span>
                        <span><strong>关系安全感低</strong>（{safeDimensions.insecurity.raw}分）</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 个性化建议 */}
            <div className="rpi-report-suggestions-box">
              <h4>💡 {type === 'self' ? '给您的建议' : '理解与应对建议'}</h4>
              <div className="rpi-report-suggestion-list">
                {type === 'self' ? (
                  <>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>尝试培养独立的兴趣爱好，增强自我价值感，减少对伴侣的过度依赖。</span>
                    </div>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>学习信任和沟通技巧，用开放的对话替代监控和猜疑行为。</span>
                    </div>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>反思控制欲的根源，可能与不安全感或过往经历有关。</span>
                    </div>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>练习情绪管理，当嫉妒冲突时，先冷静思考再做出反应。</span>
                    </div>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>关注自我成长，建立稳固的自尊和自信，减少对伴侣的需要。</span>
                    </div>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>与伴侣坦诚地讨论你的恐惧，分享你的感受和需求。</span>
                    </div>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>如果占有欲影响了关系，可以和伴侣聊聊彼此的感受，一起看看情感类的书籍或文章。</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>理解Ta的占有欲可能源于不安全感或对关系的重视，试着理解背后的情感需求。</span>
                    </div>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>主动给予Ta足够的安全感，及时回复消息，分享你的行程和感受。</span>
                    </div>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>温和地设定边界，告诉Ta哪些行为让你感到不适，寻求双方都能接受的相处方式。</span>
                    </div>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>鼓励Ta发展独立的兴趣和社交圈，帮助Ta建立自我价值感。</span>
                    </div>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>如果Ta的占有欲过强让你感到窒息，可以建议一起寻求伴侣咨询。</span>
                    </div>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>爱要相互尊重，给彼此自由的空间才能让关系更长久。</span>
                    </div>
                    <div className="rpi-report-suggestion-row">
                      <span className="rpi-report-check">✓</span>
                      <span>通过了解Ta的占有欲特点，可以更好地理解和改善你们的关系模式。</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* 四维度分析 */}
          <div className="rpi-report-section">
            <h2 className="rpi-report-section-title">
              <span className="rpi-report-section-icon">📊</span>
              四维度分析
            </h2>
            <div className="rpi-report-dimensions-grid">
              <div className="rpi-report-dimension-card">
                <div className="rpi-report-dimension-header-row">
                  <div className="rpi-report-dimension-left">
                    <span className="rpi-report-dimension-icon">🎯</span>
                    <span className="rpi-report-dimension-name">控制欲望</span>
                  </div>
                  <div className="rpi-report-dimension-score-badge" style={{ background: 'rgba(233, 32, 99, 0.1)', color: '#E92063' }}>
                    {safeDimensions.control.raw} 分
                  </div>
                </div>
                <Progress 
                  percent={safeDimensions.control.percent} 
                  strokeColor="#E92063"
                  trailColor="#F3F4F6"
                  showInfo={false}
                  strokeWidth={10}
                  style={{ marginBottom: 8 }}
                />
                <div className="rpi-report-dimension-meta">
                  <span className="rpi-report-dimension-z">Z分数: {safeDimensions.control.z}</span>
                  <span className="rpi-report-dimension-percentile">百分位: {safeDimensions.control.percent}%</span>
                </div>
                <p className="rpi-report-dimension-detail">
                  <strong>得分解读：</strong>
                  {safeDimensions.control.percent < 40 && '您尊重伴侣的自主性和个人空间，给予对方充分的信任和自由。这是健康关系的重要基础。'}
                  {safeDimensions.control.percent >= 40 && safeDimensions.control.percent < 60 && '您的控制倾向处于正常范围。偶尔会想了解伴侣的行踪，但总体上保持着适度的平衡。'}
                  {safeDimensions.control.percent >= 60 && safeDimensions.control.percent < 80 && '您对伴侣有较强的控制欲望，可能经常想知道对方在做什么，或希望参与对方的决定。建议适度放手，给予更多信任。'}
                  {safeDimensions.control.percent >= 80 && '您的控制欲较强，可能频繁监控伴侣的行为、限制社交或要求随时汇报。这可能会让伴侣感到窒息，建议寻求专业帮助调整。'}
                </p>
              </div>

              <div className="rpi-report-dimension-card">
                <div className="rpi-report-dimension-header-row">
                  <div className="rpi-report-dimension-left">
                    <span className="rpi-report-dimension-icon">💔</span>
                    <span className="rpi-report-dimension-name">嫉妒强度</span>
                  </div>
                  <div className="rpi-report-dimension-score-badge" style={{ background: 'rgba(230, 77, 102, 0.1)', color: '#E64D66' }}>
                    {safeDimensions.jealousy.raw} 分
                  </div>
                </div>
                <Progress 
                  percent={safeDimensions.jealousy.percent} 
                  strokeColor="#E64D66"
                  trailColor="#F3F4F6"
                  showInfo={false}
                  strokeWidth={10}
                  style={{ marginBottom: 8 }}
                />
                <div className="rpi-report-dimension-meta">
                  <span className="rpi-report-dimension-z">Z分数: {safeDimensions.jealousy.z}</span>
                  <span className="rpi-report-dimension-percentile">百分位: {safeDimensions.jealousy.percent}%</span>
                </div>
                <p className="rpi-report-dimension-detail">
                  <strong>得分解读：</strong>
                  {safeDimensions.jealousy.percent < 40 && '您对伴侣保持着良好的信任度，较少因嫉妒而产生负面情绪。这有助于建立安全稳定的关系氛围。'}
                  {safeDimensions.jealousy.percent >= 40 && safeDimensions.jealousy.percent < 60 && '您的嫉妒程度属于正常范围。偶尔会因伴侣与异性互动而不适，但不会过度影响关系。'}
                  {safeDimensions.jealousy.percent >= 60 && safeDimensions.jealousy.percent < 80 && '您较容易产生嫉妒情绪，可能频繁担心伴侣被吸引或出轨。建议学习情绪管理，建立更多信任。'}
                  {safeDimensions.jealousy.percent >= 80 && '您的嫉妒强度较高，可能经常想象负面场景、质问伴侣或因嫉妒争吵。建议探索嫉妒背后的不安全感根源。'}
                </p>
              </div>

              <div className="rpi-report-dimension-card">
                <div className="rpi-report-dimension-header-row">
                  <div className="rpi-report-dimension-left">
                    <span className="rpi-report-dimension-icon">💞</span>
                    <span className="rpi-report-dimension-name">情感依赖</span>
                  </div>
                  <div className="rpi-report-dimension-score-badge" style={{ background: 'rgba(217, 38, 128, 0.1)', color: '#D92680' }}>
                    {safeDimensions.dependency.raw} 分
                  </div>
                </div>
                <Progress 
                  percent={safeDimensions.dependency.percent} 
                  strokeColor="#D92680"
                  trailColor="#F3F4F6"
                  showInfo={false}
                  strokeWidth={10}
                  style={{ marginBottom: 8 }}
                />
                <div className="rpi-report-dimension-meta">
                  <span className="rpi-report-dimension-z">Z分数: {safeDimensions.dependency.z}</span>
                  <span className="rpi-report-dimension-percentile">百分位: {safeDimensions.dependency.percent}%</span>
                </div>
                <p className="rpi-report-dimension-detail">
                  <strong>得分解读：</strong>
                  {safeDimensions.dependency.percent < 40 && '您保持着良好的情感独立性，即使独处也能自我满足。您有自己的生活重心，不会过度依赖伴侣。'}
                  {safeDimensions.dependency.percent >= 40 && safeDimensions.dependency.percent < 60 && '您的情感依赖程度适中。既重视恋爱关系，也保持一定的个人空间和独立性。'}
                  {safeDimensions.dependency.percent >= 60 && safeDimensions.dependency.percent < 80 && '您对伴侣有较强的情感依赖，可能将对方视为生活中心，独处时感到焦虑。建议培养个人兴趣，建立多元支持系统。'}
                  {safeDimensions.dependency.percent >= 80 && '您高度依赖伴侣，可能觉得没有对方就不完整。建议发展独立性，学习自我关怀，避免共依存模式。'}
                </p>
              </div>

              <div className="rpi-report-dimension-card">
                <div className="rpi-report-dimension-header-row">
                  <div className="rpi-report-dimension-left">
                    <span className="rpi-report-dimension-icon">😰</span>
                    <span className="rpi-report-dimension-name">关系不安</span>
                  </div>
                  <div className="rpi-report-dimension-score-badge" style={{ background: 'rgba(236, 72, 153, 0.1)', color: '#EC4899' }}>
                    {safeDimensions.insecurity.raw} 分
                  </div>
                </div>
                <Progress 
                  percent={safeDimensions.insecurity.percent} 
                  strokeColor="#EC4899"
                  trailColor="#F3F4F6"
                  showInfo={false}
                  strokeWidth={10}
                  style={{ marginBottom: 8 }}
                />
                <div className="rpi-report-dimension-meta">
                  <span className="rpi-report-dimension-z">Z分数: {safeDimensions.insecurity.z}</span>
                  <span className="rpi-report-dimension-percentile">百分位: {safeDimensions.insecurity.percent}%</span>
                </div>
                <p className="rpi-report-dimension-detail">
                  <strong>得分解读：</strong>
                  {safeDimensions.insecurity.percent < 40 && '您对关系有较强的安全感，相信自己值得被爱，对关系的未来感到乐观。这是安全型依恋的特征。'}
                  {safeDimensions.insecurity.percent >= 40 && safeDimensions.insecurity.percent < 60 && '您的关系安全感处于正常水平。偶尔会担心关系变化，但不会过度焦虑。'}
                  {safeDimensions.insecurity.percent >= 60 && safeDimensions.insecurity.percent < 80 && '您对关系缺乏安全感，可能经常担心被抛弃或不够好。建议探索不安全感的根源，可能与早期依恋经历有关。'}
                  {safeDimensions.insecurity.percent >= 80 && '您的关系不安全感比较高，可能经常担心和焦虑。试着培养自信，多和朋友聊天，让生活更充实。记住，你很棒，值得被爱！'}
                </p>
              </div>
            </div>
          </div>

          {/* 可视化分析 - 双雷达图 */}
          <div className="rpi-report-section">
            <h2 className="rpi-report-section-title">
              <span className="rpi-report-section-icon">📊</span>
              可视化分析
            </h2>
            <div className="rpi-report-radars-container">
              {/* 恋爱占有欲雷达图 */}
              <div className="rpi-report-radar-card">
                <h3 className="rpi-report-radar-title">恋爱占有欲雷达图</h3>
                <div className="rpi-report-radar-chart">
                  <svg viewBox="-60 -60 420 420" className="rpi-report-radar-svg">
                    {/* 背景网格 */}
                    <polygon points="150,30 270,150 150,270 30,150" fill="#FAFAFA" stroke="#E5E7EB" strokeWidth="1.5"/>
                    <polygon points="150,60 240,150 150,240 60,150" fill="#F5F5F5" stroke="#E5E7EB" strokeWidth="1.5"/>
                    <polygon points="150,90 210,150 150,210 90,150" fill="#FFFFFF" stroke="#E5E7EB" strokeWidth="1.5"/>
                    <polygon points="150,120 180,150 150,180 120,150" fill="#FAFAFA" stroke="#E5E7EB" strokeWidth="1"/>
                    
                    {/* 中心点 */}
                    <circle cx="150" cy="150" r="3" fill="#9CA3AF"/>
                    
                    {/* 轴线 */}
                    <line x1="150" y1="150" x2="150" y2="30" stroke="#D1D5DB" strokeWidth="1"/>
                    <line x1="150" y1="150" x2="270" y2="150" stroke="#D1D5DB" strokeWidth="1"/>
                    <line x1="150" y1="150" x2="150" y2="270" stroke="#D1D5DB" strokeWidth="1"/>
                    <line x1="150" y1="150" x2="30" y2="150" stroke="#D1D5DB" strokeWidth="1"/>
                    
                    {/* 刻度标签 */}
                    <text x="150" y="145" textAnchor="middle" fill="#9CA3AF" fontSize="10">20</text>
                    <text x="150" y="115" textAnchor="middle" fill="#9CA3AF" fontSize="10">40</text>
                    <text x="150" y="85" textAnchor="middle" fill="#9CA3AF" fontSize="10">60</text>
                    <text x="150" y="55" textAnchor="middle" fill="#9CA3AF" fontSize="10">80</text>
                    
                    {/* 数据多边形（4个点，按上右下左顺序） */}
                    <polygon 
                      points={`150,${150-(safeDimensions.control.percent*1.2)} ${150+(safeDimensions.jealousy.percent*1.2)},150 150,${150+(safeDimensions.dependency.percent*1.2)} ${150-(safeDimensions.insecurity.percent*1.2)},150`}
                      fill="rgba(233, 32, 99, 0.3)" 
                      stroke="#E92063" 
                      strokeWidth="3"
                    />
                    
                    {/* 数据点 */}
                    <circle cx="150" cy={150-(safeDimensions.control.percent*1.2)} r="6" fill="#E92063" stroke="white" strokeWidth="2"/>
                    <circle cx={150+(safeDimensions.jealousy.percent*1.2)} cy="150" r="6" fill="#E92063" stroke="white" strokeWidth="2"/>
                    <circle cx="150" cy={150+(safeDimensions.dependency.percent*1.2)} r="6" fill="#E92063" stroke="white" strokeWidth="2"/>
                    <circle cx={150-(safeDimensions.insecurity.percent*1.2)} cy="150" r="6" fill="#E92063" stroke="white" strokeWidth="2"/>
                    
                    {/* 维度标签 */}
                    <text x="150" y="10" textAnchor="middle" fill="#333" fontSize="14" fontWeight="600">控制欲望</text>
                    <text x="150" y="24" textAnchor="middle" fill="#E92063" fontSize="12" fontWeight="bold">{safeDimensions.control.percent}</text>
                    
                    <text x="295" y="155" textAnchor="start" fill="#333" fontSize="14" fontWeight="600">嫉妒强度</text>
                    <text x="295" y="169" textAnchor="start" fill="#E92063" fontSize="12" fontWeight="bold">{safeDimensions.jealousy.percent}</text>
                    
                    <text x="150" y="295" textAnchor="middle" fill="#333" fontSize="14" fontWeight="600">情感依赖</text>
                    <text x="150" y="309" textAnchor="middle" fill="#E92063" fontSize="12" fontWeight="bold">{safeDimensions.dependency.percent}</text>
                    
                    <text x="5" y="155" textAnchor="end" fill="#333" fontSize="14" fontWeight="600">关系不安</text>
                    <text x="5" y="169" textAnchor="end" fill="#E92063" fontSize="12" fontWeight="bold">{safeDimensions.insecurity.percent}</text>
                  </svg>
                </div>
                <div className="rpi-report-radar-desc">
                  T分数范围：20-80，50为平均水平
                </div>
              </div>

              {/* 依恋类型雷达图 */}
              <div className="rpi-report-radar-card">
                <h3 className="rpi-report-radar-title">依恋类型雷达图</h3>
                <div className="rpi-report-radar-chart">
                  <svg viewBox="-60 -60 420 420" className="rpi-report-radar-svg">
                    {/* 背景网格 */}
                    <polygon points="150,30 270,150 150,270 30,150" fill="#FAFAFA" stroke="#E5E7EB" strokeWidth="1.5"/>
                    <polygon points="150,60 240,150 150,240 60,150" fill="#F5F5F5" stroke="#E5E7EB" strokeWidth="1.5"/>
                    <polygon points="150,90 210,150 150,210 90,150" fill="#FFFFFF" stroke="#E5E7EB" strokeWidth="1.5"/>
                    <polygon points="150,120 180,150 150,180 120,150" fill="#FAFAFA" stroke="#E5E7EB" strokeWidth="1"/>
                    
                    {/* 中心点 */}
                    <circle cx="150" cy="150" r="3" fill="#9CA3AF"/>
                    
                    {/* 轴线 */}
                    <line x1="150" y1="150" x2="150" y2="30" stroke="#D1D5DB" strokeWidth="1"/>
                    <line x1="150" y1="150" x2="270" y2="150" stroke="#D1D5DB" strokeWidth="1"/>
                    <line x1="150" y1="150" x2="150" y2="270" stroke="#D1D5DB" strokeWidth="1"/>
                    <line x1="150" y1="150" x2="30" y2="150" stroke="#D1D5DB" strokeWidth="1"/>
                    
                    {/* 刻度标签 */}
                    <text x="150" y="145" textAnchor="middle" fill="#9CA3AF" fontSize="10">20</text>
                    <text x="150" y="115" textAnchor="middle" fill="#9CA3AF" fontSize="10">40</text>
                    <text x="150" y="85" textAnchor="middle" fill="#9CA3AF" fontSize="10">60</text>
                    <text x="150" y="55" textAnchor="middle" fill="#9CA3AF" fontSize="10">80</text>
                    
                    {/* 数据多边形 - 依恋类型（基于不安全感和依赖度计算） */}
                    {(() => {
                      // 依恋理论四种类型的计算
                      // 焦虑维度 = insecurity (关系不安全感)
                      // 回避维度 = 100 - dependency (低依赖 = 高回避)
                      const anxietyScore = safeDimensions.insecurity.percent; // 焦虑维度
                      const avoidanceScore = 100 - safeDimensions.dependency.percent; // 回避维度
                      
                      // 四种依恋类型的匹配强度
                      const secureScore = Math.round((100 - anxietyScore + safeDimensions.dependency.percent) / 2); // 低焦虑 + 高依赖（低回避）
                      const anxiousScore = Math.round((anxietyScore + safeDimensions.dependency.percent) / 2); // 高焦虑 + 高依赖（低回避）
                      const avoidantScore = Math.round(((100 - anxietyScore) + avoidanceScore) / 2); // 低焦虑 + 高回避（低依赖）
                      const fearfulScore = Math.round((anxietyScore + avoidanceScore) / 2); // 高焦虑 + 高回避（低依赖）
                      
                      return (
                        <>
                          <polygon 
                            points={`150,${150-(secureScore*1.2)} ${150+(anxiousScore*1.2)},150 150,${150+(avoidantScore*1.2)} ${150-(fearfulScore*1.2)},150`}
                            fill="rgba(233, 32, 99, 0.3)" 
                            stroke="#E92063" 
                            strokeWidth="3"
                          />
                          
                          {/* 数据点 */}
                          <circle cx="150" cy={150-(secureScore*1.2)} r="6" fill="#E92063" stroke="white" strokeWidth="2"/>
                          <circle cx={150+(anxiousScore*1.2)} cy="150" r="6" fill="#E92063" stroke="white" strokeWidth="2"/>
                          <circle cx="150" cy={150+(avoidantScore*1.2)} r="6" fill="#E92063" stroke="white" strokeWidth="2"/>
                          <circle cx={150-(fearfulScore*1.2)} cy="150" r="6" fill="#E92063" stroke="white" strokeWidth="2"/>
                          
                          {/* 维度标签 */}
                          <text x="150" y="10" textAnchor="middle" fill="#333" fontSize="14" fontWeight="600">安全型</text>
                          <text x="150" y="24" textAnchor="middle" fill="#666" fontSize="11">{secureScore}</text>
                          
                          <text x="295" y="155" textAnchor="start" fill="#333" fontSize="14" fontWeight="600">焦虑型</text>
                          <text x="295" y="169" textAnchor="start" fill="#666" fontSize="11">{anxiousScore}</text>
                          
                          <text x="150" y="295" textAnchor="middle" fill="#333" fontSize="14" fontWeight="600">回避型</text>
                          <text x="150" y="309" textAnchor="middle" fill="#666" fontSize="11">{avoidantScore}</text>
                          
                          <text x="5" y="155" textAnchor="end" fill="#333" fontSize="14" fontWeight="600">恐惧型</text>
                          <text x="5" y="169" textAnchor="end" fill="#666" fontSize="11">{fearfulScore}</text>
                        </>
                      );
                    })()}
                  </svg>
                </div>
                <div className="rpi-report-radar-desc">
                  主导类型：<span style={{ color: '#E92063', fontWeight: 'bold' }}>
                    {(() => {
                      // 重新计算四种类型的分数以确定主导类型
                      const anxietyScore = safeDimensions.insecurity.percent;
                      const avoidanceScore = 100 - safeDimensions.dependency.percent;
                      
                      const secureScore = Math.round((100 - anxietyScore + safeDimensions.dependency.percent) / 2);
                      const anxiousScore = Math.round((anxietyScore + safeDimensions.dependency.percent) / 2);
                      const avoidantScore = Math.round(((100 - anxietyScore) + avoidanceScore) / 2);
                      const fearfulScore = Math.round((anxietyScore + avoidanceScore) / 2);
                      
                      // 找出最高分
                      const maxScore = Math.max(secureScore, anxiousScore, avoidantScore, fearfulScore);
                      const types = [];
                      
                      if (secureScore === maxScore) types.push('安全型');
                      if (anxiousScore === maxScore) types.push('焦虑型');
                      if (avoidantScore === maxScore) types.push('回避型');
                      if (fearfulScore === maxScore) types.push('恐惧型');
                      
                      return types.join(' + ') || '混合型';
                    })()}
                  </span>
                </div>
                <div className="rpi-report-radar-desc" style={{ fontSize: '12px', marginTop: '4px', color: '#666' }}>
                  核心维度：焦虑 {safeDimensions.insecurity.percent}% · 回避 {100 - safeDimensions.dependency.percent}%
                </div>
                <div className="rpi-report-radar-desc" style={{ fontSize: '11px', marginTop: '8px', padding: '8px', background: '#FFF5F7', borderRadius: '6px', lineHeight: '1.6' }}>
                  <strong>💡 图表说明：</strong>四个维度表示您与各类型的"匹配度"。<br/>
                  • <strong>焦虑型</strong>（高焦虑+高依赖）：{Math.round((safeDimensions.insecurity.percent + safeDimensions.dependency.percent) / 2)}%<br/>
                  • <strong>回避型</strong>（低焦虑+低依赖）：{Math.round(((100 - safeDimensions.insecurity.percent) + (100 - safeDimensions.dependency.percent)) / 2)}%<br/>
                  • <strong>安全型</strong>（低焦虑+高依赖）：{Math.round((100 - safeDimensions.insecurity.percent + safeDimensions.dependency.percent) / 2)}%<br/>
                  • <strong>恐惧型</strong>（高焦虑+低依赖）：{Math.round((safeDimensions.insecurity.percent + (100 - safeDimensions.dependency.percent)) / 2)}%
                </div>
              </div>
            </div>
          </div>

          {/* 恋爱占有欲人格类型 */}
          <div className="rpi-report-section">
            <h2 className="rpi-report-section-title">
              <span className="rpi-report-section-icon">✨</span>
              恋爱占有欲人格类型
            </h2>
            {(() => {
              // 根据 RPI 分数确定人格类型
              let personalityType;
              if (rpiIndex >= 80) {
                personalityType = {
                  icon: '🔗',
                  name: '共依存融合型',
                  subtitle: '与伴侣共依存，界限模糊，失去自我，需要重建独立性',
                  traits: ['无法分离', '界限模糊', '自我消失', '过度依赖', '情感绑架'],
                  strengths: ['关系紧密', '情感投入深'],
                  warnings: ['共依存关系', '双方都可能不健康', '需要重建自我', '易产生窒息感'],
                  advice: '你们的关系非常紧密！不妨尝试培养一些个人爱好，和朋友多聚聚，保持独立的生活空间。健康的爱情需要两个完整的人，而不是半个人拼在一起哦！'
                };
              } else if (rpiIndex >= 65) {
                personalityType = {
                  icon: '⚡',
                  name: '强控制依赖型',
                  subtitle: '控制欲强烈，高度依赖伴侣，需要学会放手和信任',
                  traits: ['控制欲强', '嫉妒敏感', '需要掌控', '缺乏安全感', '过度监控'],
                  strengths: ['关系投入高', '重视承诺', '责任心强'],
                  warnings: ['易引发冲突', '伴侣感到压力', '关系紧张', '需要学会信任'],
                  advice: '你的占有欲有点强哦！试着多信任对方，给彼此一些自由空间。培养自己的兴趣爱好，让生活更丰富。相信你们的感情，放松一点会更好！'
                };
              } else if (rpiIndex >= 50) {
                personalityType = {
                  icon: '💫',
                  name: '焦虑依恋型',
                  subtitle: '对关系焦虑，需要频繁确认，但仍保持一定独立性',
                  traits: ['需要确认', '关系焦虑', '情绪波动', '敏感多疑', '占有适中'],
                  strengths: ['情感真挚', '重视关系', '愿意沟通', '有自我意识'],
                  warnings: ['过度担忧', '情绪消耗大', '需要学会自我安抚', '建立自信'],
                  advice: '你的焦虑型依恋可能让你在关系中感到不安。建议学习正念冥想和情绪管理技巧，提升自我价值感。《依恋》一书能帮助你更好地理解自己。'
                };
              } else if (rpiIndex >= 35) {
                personalityType = {
                  icon: '🌸',
                  name: '健康平衡型',
                  subtitle: '占有欲适度，能平衡亲密与独立，关系健康和谐',
                  traits: ['界限清晰', '信任伴侣', '情绪稳定', '独立自主', '适度关注'],
                  strengths: ['关系和谐', '相互尊重', '自我完整', '沟通良好', '信任基础好'],
                  warnings: ['保持现状', '继续成长', '警惕倦怠期'],
                  advice: '你拥有健康的恋爱模式！建议继续保持这种平衡状态，同时可以阅读《亲密关系》等书籍，持续提升关系质量。'
                };
              } else if (rpiIndex >= 20) {
                personalityType = {
                  icon: '🦋',
                  name: '独立自主型',
                  subtitle: '占有欲较低，高度独立，需要注意情感连接',
                  traits: ['高度独立', '占有欲弱', '理性冷静', '重视空间', '情感淡化'],
                  strengths: ['独立性强', '不黏人', '给予自由', '成熟理性', '自我完整'],
                  warnings: ['情感连接弱', '伴侣可能感到被忽视', '需要增加亲密感', '平衡独立与亲密'],
                  advice: '你的独立性很强，但要注意不要让伴侣感到被忽视。建议适当表达关心和在乎，学习《爱的五种语言》，找到适合你们的亲密方式。'
                };
              } else {
                personalityType = {
                  icon: '🌙',
                  name: '回避疏离型',
                  subtitle: '占有欲极低，情感疏离，可能存在回避型依恋',
                  traits: ['情感疏离', '回避亲密', '冷漠淡然', '自我保护', '距离感强'],
                  strengths: ['独立能力强', '不依赖他人', '理性客观'],
                  warnings: ['难以建立深度连接', '伴侣感到孤独', '关系难以维系', '需要打开心扉'],
                  advice: '你是个很独立的人！不过有时候也可以适当向伴侣敞开心扉，分享你的感受。试着表达情感，会让关系更亲密哦。记住，示弱不是软弱，是信任的表现！'
                };
              }

              return (
                <div className="rpi-report-personality-card">
                  <div className="rpi-report-personality-icon">
                    <span style={{ fontSize: 48 }}>{personalityType.icon}</span>
                  </div>
                  <h3 className="rpi-report-personality-type">{personalityType.name}</h3>
                  <p className="rpi-report-personality-subtitle">
                    {personalityType.subtitle}
                  </p>
                  
                  <div className="rpi-report-personality-details">
                    <div className="rpi-report-personality-section">
                      <h4>✨ 核心特征</h4>
                      <ul>
                        {personalityType.traits.map((trait, idx) => (
                          <li key={idx}>{trait}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="rpi-report-personality-section rpi-report-personality-positive">
                      <h4>😊 {getPronouns().possessive}的优势</h4>
                      <ul>
                        {personalityType.strengths.map((strength, idx) => (
                          <li key={idx}>{strength}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="rpi-report-personality-section rpi-report-personality-warning">
                      <h4>⚠️ 需要注意的方面</h4>
                      <ul>
                        {personalityType.warnings.map((warning, idx) => (
                          <li key={idx}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="rpi-report-personality-advice">
                    <span className="rpi-report-advice-icon">💡</span>
                    <strong>专业建议</strong>
                    <p>{personalityType.advice}</p>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* 依恋模式分析 */}
          <div className="rpi-report-section">
            <h2 className="rpi-report-section-title">
              <span className="rpi-report-section-icon">🧠</span>
              依恋模式分析
            </h2>
            {(() => {
              // 根据 RPI 分数确定依恋模式
              let attachmentType;
              if (rpiIndex >= 70) {
                attachmentType = {
                  icon: '💗',
                  name: '焦虑型依恋',
                  subtitle: '对关系高度焦虑，需要不断确认被爱，对分离高度敏感',
                  manifestations: '需要频繁的关注和确认；担心被抛弃；对伴侣的小变化敏感；情绪过度依赖伴侣；经常查看伴侣手机和社交媒体；要求伴侣随时报告行踪。',
                  causes: '早期与照顾者的关系不稳定，得到的回应时而温暖时而冷漠，形成"我不值得被爱"的内在模式。可能经历过父母情感忽视、频繁分离或不可预测的养育方式。',
                  improvement: '建立稳定的自我价值感；学习情绪自我调节；在安全关系中获得矫正性体验；通过冥想和正念练习降低焦虑；与伴侣坦诚沟通内心恐惧，建立安全承诺。'
                };
              } else if (rpiIndex >= 50) {
                attachmentType = {
                  icon: '🔄',
                  name: '矛盾型依恋',
                  subtitle: '在亲密与独立之间摇摆，既渴望又害怕关系',
                  manifestations: '时而非常黏人，时而疏远冷淡；对伴侣的态度反复无常；既需要确认又抗拒亲密；内心矛盾纠结；担心失去自我又害怕失去对方。',
                  causes: '童年时期父母的养育方式不一致，有时过度保护有时放任不管，导致对关系的矛盾心理。可能经历过被侵入隐私或被过度控制的经历。',
                  improvement: '试着找到亲密与独立的平衡点；和伴侣坦诚沟通自己的需求；保持真实的自己；记住爱一个人不需要失去自我；给自己一些时间慢慢调整。'
                };
              } else if (rpiIndex >= 30) {
                attachmentType = {
                  icon: '🌟',
                  name: '安全型依恋',
                  subtitle: '情感稳定，能够平衡亲密与独立，信任关系',
                  manifestations: '能够舒适地表达情感和需求；信任伴侣；不过度依赖也不回避亲密；冲突后能有效修复；情绪稳定；给予对方空间的同时保持连接。',
                  causes: '童年时期得到了稳定、温暖、可预测的照顾，父母能够及时回应需求，建立了"我值得被爱、他人值得信任"的内在模式。',
                  improvement: '继续保持这种健康的依恋模式；不断深化自我认知；在关系中持续学习和成长；培养更丰富的情感表达能力；成为伴侣的安全港湾。'
                };
              } else {
                attachmentType = {
                  icon: '🌙',
                  name: '回避型依恋',
                  subtitle: '回避亲密和情感表达，强调独立，不愿依赖他人',
                  manifestations: '不愿分享内心真实感受；当关系过于亲密时会退缩；强调自给自足；难以承诺和信任；用理性压抑情感；保持情感距离；避免冲突和深度沟通。',
                  causes: '童年时期情感需求常被忽视或拒绝，学会了压抑需求和情感。可能经历过早期分离、情感冷漠的养育环境，或被教导"不要依赖他人"。',
                  improvement: '试着向伴侣多表达你的感受；允许自己偶尔示弱；慢慢学会信任；记住亲密关系会让生活更美好；不要什么都自己扛，有人陪伴也挺好的！'
                };
              }

              return (
                <div className="rpi-report-attachment-card">
                  <div className="rpi-report-attachment-type">
                    <div className="rpi-report-attachment-icon">{attachmentType.icon}</div>
                    <h3>{attachmentType.name}</h3>
                    <p>{attachmentType.subtitle}</p>
                  </div>
                  <div className="rpi-report-attachment-details">
                    <div className="rpi-report-attachment-item">
                      <strong>典型表现：</strong>
                      <p>{attachmentType.manifestations}</p>
                    </div>
                    <div className="rpi-report-attachment-item">
                      <strong>形成原因：</strong>
                      <p>{attachmentType.causes}</p>
                    </div>
                    <div className="rpi-report-attachment-item">
                      <strong>改善方向：</strong>
                      <p>{attachmentType.improvement}</p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* 影响路径分析 */}
          <div className="rpi-report-section">
            <h2 className="rpi-report-section-title">
              <span className="rpi-report-section-icon">🔄</span>
              占有欲影响路径分析
            </h2>
            <div className="rpi-report-path-card">
              <p className="rpi-report-path-intro">
                占有欲的形成和影响是一个复杂的心理过程，从深层根源到最终的关系影响，
                经历了多个层面的转化。理解这个路径有助于找到改善的切入点。
              </p>
              <div className="rpi-report-path-flow">
                <div className="rpi-report-path-item rpi-report-path-root">
                  <span className="rpi-report-path-number">1</span>
                  <div className="rpi-report-path-content">
                    <span className="rpi-report-path-label">根源层面</span>
                    <span className="rpi-report-path-text">不安全依恋 + 低自尊 + 过往创伤</span>
                    <span className="rpi-report-path-desc">童年与照顾者的关系、过往被背叛的经历、自我价值感不足</span>
                  </div>
                </div>
                <div className="rpi-report-path-arrow">↓</div>
                <div className="rpi-report-path-item rpi-report-path-cognition">
                  <span className="rpi-report-path-number">2</span>
                  <div className="rpi-report-path-content">
                    <span className="rpi-report-path-label">认知层面</span>
                    <span className="rpi-report-path-text">怀疑思维 + 威胁评估 + 归因偏差</span>
                    <span className="rpi-report-path-desc">过度解读伴侣行为、将正常社交视为威胁、灾难化思维</span>
                  </div>
                </div>
                <div className="rpi-report-path-arrow">↓</div>
                <div className="rpi-report-path-item rpi-report-path-emotion">
                  <span className="rpi-report-path-number">3</span>
                  <div className="rpi-report-path-content">
                    <span className="rpi-report-path-label">情绪层面</span>
                    <span className="rpi-report-path-text">焦虑 + 嫉妒 + 恐惧 + 愤怒</span>
                    <span className="rpi-report-path-desc">持续的不安、强烈的嫉妒情绪、被抛弃的恐惧</span>
                  </div>
                </div>
                <div className="rpi-report-path-arrow">↓</div>
                <div className="rpi-report-path-item rpi-report-path-behavior">
                  <span className="rpi-report-path-number">4</span>
                  <div className="rpi-report-path-content">
                    <span className="rpi-report-path-label">行为层面</span>
                    <span className="rpi-report-path-text">监控 + 限制 + 控制 + 质问</span>
                    <span className="rpi-report-path-desc">查看手机、限制社交、要求汇报、频繁质问</span>
                  </div>
                </div>
                <div className="rpi-report-path-arrow">↓</div>
                <div className="rpi-report-path-item rpi-report-path-impact">
                  <span className="rpi-report-path-number">5</span>
                  <div className="rpi-report-path-content">
                    <span className="rpi-report-path-label">关系影响</span>
                    <span className="rpi-report-path-text">信任受损 + 冲突增加 + 亲密度下降</span>
                    <span className="rpi-report-path-desc">伴侣感到窒息、关系满意度降低、可能导致分手</span>
                  </div>
                </div>
              </div>
              
              <div className="rpi-report-path-intervention">
                <h4>🎯 干预切入点</h4>
                <p>
                  改善占有欲可以从任何层面入手：处理童年创伤（根源）、纠正认知扭曲（认知）、
                  学习情绪管理（情绪）、改变控制行为（行为）、改善沟通模式（关系）。
                  建议从最容易改变的行为层面开始，逐步深入到认知和情绪层面。
                </p>
              </div>
            </div>
          </div>

          {/* 爱情心理学专家建议 */}
          <div className="rpi-report-section">
            <h2 className="rpi-report-section-title">
              <span className="rpi-report-section-icon">💡</span>
              爱情心理学专家建议
            </h2>
            <div className="rpi-report-expert-cards">
              {/* 控制欲望管理 */}
              <div className="rpi-report-expert-card">
                <div className="rpi-report-expert-icon" style={{ background: 'rgba(233, 32, 99, 0.1)' }}>
                  <span>🎯</span>
                </div>
                <h3>控制欲望管理</h3>
                <p className="rpi-report-expert-intro">
                  你表现出较高的控制倾向。心理学研究表明，过度控制往往源于内心的不安全感。建议：
                </p>
                <div className="rpi-report-expert-steps">
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">①</span>
                    <span>反思控制欲的根源，可能与童年经历或过往被背叛经验有关；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">②</span>
                    <span>学习信任的艺术，给予伴侣信任空间；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">③</span>
                    <span>用开放对话代替监控，表达需求而非控制行为；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">④</span>
                    <span>培养自己的兴趣爱好，建立独立的生活圈子。</span>
                  </div>
                </div>
              </div>

              {/* 嫉妒情绪调节 */}
              <div className="rpi-report-expert-card">
                <div className="rpi-report-expert-icon" style={{ background: 'rgba(230, 77, 102, 0.1)' }}>
                  <span>💔</span>
                </div>
                <h3>嫉妒情绪调节</h3>
                <p className="rpi-report-expert-intro">
                  你的嫉妒倾向较为明显。适度嫉妒是在乎的表现，但过度嫉妒会伤害关系。建议：
                </p>
                <div className="rpi-report-expert-steps">
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">①</span>
                    <span>练习情绪觉察，当嫉妒出现时先暂停，思考；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">②</span>
                    <span>挑战嫉妒背后的非理性信念，"Ta和异性说话≠Ta不爱我"；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">③</span>
                    <span>用"我感到..."表达而非指责创伤有关；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">④</span>
                    <span>提升自我价值感，相信自己值得被爱；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">⑤</span>
                    <span>与伴侣坦诚沟通恐惧，寻求理解和支持；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">⑥</span>
                    <span>了解嫉妒炉是正常情绪，但可以选择如何回应。</span>
                  </div>
                </div>
              </div>

              {/* 情感独立成长 */}
              <div className="rpi-report-expert-card">
                <div className="rpi-report-expert-icon" style={{ background: 'rgba(217, 38, 128, 0.1)' }}>
                  <span>👥</span>
                </div>
                <h3>情感独立成长</h3>
                <p className="rpi-report-expert-intro">
                  你对伴侣的情感依赖较高。健康的关系需要两个完整的个体。建议：
                </p>
                <div className="rpi-report-expert-steps">
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">①</span>
                    <span>培养独立的兴趣和朋友圈，丰富你的生活；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">②</span>
                    <span>练习独处，学会享受一个人的时光；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">③</span>
                    <span>建立多元的情感支持系统（朋友、家人），不要只依赖伴侣；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">④</span>
                    <span>提升自我价值感，不要将自我完整性寄托在关系上；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">⑤</span>
                    <span>学习情绪自我调节，不过度依赖伴侣安慰；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">⑥</span>
                    <span>阅读共依赖相关书籍，了解健康依恋模式。</span>
                  </div>
                </div>
              </div>

              {/* 关系安全感建立 */}
              <div className="rpi-report-expert-card">
                <div className="rpi-report-expert-icon" style={{ background: 'rgba(251, 191, 36, 0.1)' }}>
                  <span>🛡️</span>
                </div>
                <h3>关系安全感建立</h3>
                <p className="rpi-report-expert-intro">
                  你在关系中存在较强的不安全感，这可能源于焦虑型依恋或过往创伤。建议：
                </p>
                <div className="rpi-report-expert-steps">
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">①</span>
                    <span>探索不安全感的根源，是童年经历还是过往关系伤害？</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">②</span>
                    <span>挑战"我不够好"的负面信念，列举自己的优点；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">③</span>
                    <span>练习正念和自我关怀，减少过度担忧；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">④</span>
                    <span>与伴侣开放沟通恐惧，寻求理解和支持；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">⑤</span>
                    <span>如果不安全感让你很困扰，可以和信任的朋友或伴侣聊聊；</span>
                  </div>
                  <div className="rpi-report-expert-step">
                    <span className="rpi-report-step-number">⑥</span>
                    <span>记录伴侣值得信的证据，在焦虑时翻看提醒自己。</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 星座参考 */}
          {(() => {
            console.log('检查星座显示条件:', reportData.demographic);
            const hasZodiac = reportData.demographic && reportData.demographic.zodiac && reportData.demographic.zodiac !== 'unknown';
            console.log('是否有星座:', hasZodiac, reportData.demographic?.zodiac);
            
            // 如果没有星座信息，显示提示
            if (!hasZodiac) {
              return (
                <div className="rpi-report-section">
                  <h2 className="rpi-report-section-title">
                    <span className="rpi-report-section-icon">⭐</span>
                    星座性格参考
                    <span className="rpi-report-section-badge">仅供娱乐</span>
                  </h2>
                  <div className="rpi-report-zodiac-card">
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <div style={{ fontSize: 48, marginBottom: 16 }}>🌟</div>
                      <h3 style={{ color: '#666', marginBottom: 12 }}>未提供星座信息</h3>
                      <p style={{ color: '#999', lineHeight: 1.6 }}>
                        您在填写基本信息时未选择星座。星座参考部分将不会显示。<br/>
                        如需查看星座分析，可以重新进行测试并填写星座信息。
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            
            const zodiacInfo = getZodiacInfo(reportData.demographic.zodiac);
            console.log('星座信息:', zodiacInfo);
            if (!zodiacInfo) return null;
            
            return (
              <div className="rpi-report-section">
                <h2 className="rpi-report-section-title">
                  <span className="rpi-report-section-icon">⭐</span>
                  星座性格参考
                  <span className="rpi-report-section-badge">仅供娱乐</span>
                </h2>
                <div className="rpi-report-zodiac-card">
                  {/* 星座标题 */}
                  <div className="rpi-report-zodiac-title-row">
                    <span className="rpi-report-zodiac-emoji">{zodiacInfo.emoji}</span>
                    <h3>{zodiacInfo.name}</h3>
                  </div>

                  {/* 星座标签 */}
                  <div className="rpi-report-zodiac-tags">
                    {zodiacInfo.tags.map((tag, index) => (
                      <span key={index} className="rpi-report-zodiac-tag">{tag}</span>
                    ))}
                  </div>

                  {/* 恋爱特点 */}
                  <div className="rpi-report-zodiac-section">
                    <h4>💜 {zodiacInfo.name}在恋爱中的特点</h4>
                    <p>{zodiacInfo.traits}</p>
                  </div>

                  {/* 星座建议 */}
                  <div className="rpi-report-zodiac-section">
                    <h4>💡 给你的星座建议</h4>
                    <p>{zodiacInfo.advice}</p>
                  </div>

                  {/* 特别说明 */}
                  <div className="rpi-report-zodiac-disclaimer">
                    <span className="rpi-report-zodiac-disclaimer-icon">ℹ️</span>
                    <p>
                      <strong>特别说明：</strong>
                      星座性格没有科学依据，以上内容仅作为文化和星座传承，纯属娱乐参考。
                      您的RPI评估结果全基于心理学理论和实际问卷，与星座无关。每个人都是独特的个体，不应被星座标签所限制。
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* 关系健康度评估 */}
          <div className="rpi-report-section">
            <h2 className="rpi-report-section-title">
              <span className="rpi-report-section-icon">❤️</span>
              关系健康度评估
            </h2>
            <div className="rpi-report-health-card">
              <p className="rpi-report-health-intro">
                基于您的RPI指数和四维度分析，我们对您当前的关系健康状况进行综合评估：
              </p>
              
              <div className="rpi-report-health-indicators">
                <div className={`rpi-report-health-item ${rpiIndex >= 70 ? 'health-warning' : rpiIndex >= 50 ? 'health-caution' : 'health-good'}`}>
                  <div className="rpi-report-health-icon">💕</div>
                  <div className="rpi-report-health-content">
                    <h4>情感连接度</h4>
                    <div className="rpi-report-health-bar">
                      <div className="rpi-report-health-fill" style={{ width: `${Math.min(safeDimensions.dependency.percent, 100)}%` }}></div>
                    </div>
                    <p>{safeDimensions.dependency.percent >= 80 ? '依赖度很高，需要培养独立性' : safeDimensions.dependency.percent >= 60 ? '依赖度较高，注意保持自我' : safeDimensions.dependency.percent >= 30 ? '连接适度，保持平衡' : '连接较弱，可增加互动'}</p>
                  </div>
                </div>

                <div className={`rpi-report-health-item ${safeDimensions.control.percent >= 80 ? 'health-warning' : safeDimensions.control.percent >= 60 ? 'health-caution' : 'health-good'}`}>
                  <div className="rpi-report-health-icon">🎯</div>
                  <div className="rpi-report-health-content">
                    <h4>控制平衡度</h4>
                    <div className="rpi-report-health-bar">
                      <div className="rpi-report-health-fill" style={{ width: `${100 - Math.min(safeDimensions.control.percent, 100)}%` }}></div>
                    </div>
                    <p>{safeDimensions.control.percent >= 80 ? '控制欲过强，需要学会放手' : safeDimensions.control.percent >= 60 ? '控制倾向明显，注意平衡' : safeDimensions.control.percent >= 30 ? '控制适度，继续保持' : '控制欲低，关系自由'}</p>
                  </div>
                </div>

                <div className={`rpi-report-health-item ${safeDimensions.insecurity.percent >= 80 ? 'health-warning' : safeDimensions.insecurity.percent >= 60 ? 'health-caution' : 'health-good'}`}>
                  <div className="rpi-report-health-icon">🛡️</div>
                  <div className="rpi-report-health-content">
                    <h4>安全感指数</h4>
                    <div className="rpi-report-health-bar">
                      <div className="rpi-report-health-fill" style={{ width: `${100 - Math.min(safeDimensions.insecurity.percent, 100)}%` }}></div>
                    </div>
                    <p>{safeDimensions.insecurity.percent >= 80 ? '安全感很低，建议培养自信' : safeDimensions.insecurity.percent >= 60 ? '安全感偏低，可以提升' : safeDimensions.insecurity.percent >= 30 ? '安全感中等，继续保持' : '安全感强，关系稳固'}</p>
                  </div>
                </div>

                <div className={`rpi-report-health-item ${safeDimensions.jealousy.percent >= 80 ? 'health-warning' : safeDimensions.jealousy.percent >= 60 ? 'health-caution' : 'health-good'}`}>
                  <div className="rpi-report-health-icon">😊</div>
                  <div className="rpi-report-health-content">
                    <h4>情绪稳定性</h4>
                    <div className="rpi-report-health-bar">
                      <div className="rpi-report-health-fill" style={{ width: `${100 - Math.min(safeDimensions.jealousy.percent, 100)}%` }}></div>
                    </div>
                    <p>{safeDimensions.jealousy.percent >= 80 ? '嫉妒情绪强烈，影响关系' : safeDimensions.jealousy.percent >= 60 ? '嫉妒倾向明显，需要调节' : safeDimensions.jealousy.percent >= 30 ? '情绪较稳定，偶有波动' : '情绪稳定，心态平和'}</p>
                  </div>
                </div>
              </div>

              <div className="rpi-report-health-summary">
                <h4>🎯 综合评价</h4>
                <p>
                  {rpiIndex >= 80 && <>您的关系目前处于<strong>高风险状态</strong>，占有欲过强可能已经严重影响了双方的幸福感。建议尽快采取行动，寻求专业帮助。</>}
                  {rpiIndex >= 65 && rpiIndex < 80 && <>您的关系存在<strong>一定风险</strong>，占有欲较强可能会给伴侣带来压力。建议学习情绪管理和沟通技巧，改善互动模式。</>}
                  {rpiIndex >= 50 && rpiIndex < 65 && <>您的关系处于<strong>警戒状态</strong>，占有欲有些偏高，需要注意平衡亲密与独立。建议自我反思并与伴侣沟通。</>}
                  {rpiIndex >= 35 && rpiIndex < 50 && <>您的关系总体<strong>健康良好</strong>，占有欲适度，能够平衡亲密与自由。继续保持并不断提升关系质量。</>}
                  {rpiIndex < 35 && <>您的关系中占有欲较低，总体<strong>自由健康</strong>，但也要注意保持适度的情感连接，避免过于疏离。</>}
                </p>
              </div>
            </div>
          </div>

          {/* 沟通技巧指南 */}
          <div className="rpi-report-section">
            <h2 className="rpi-report-section-title">
              <span className="rpi-report-section-icon">💬</span>
              有效沟通技巧指南
            </h2>
            <div className="rpi-report-communication-card">
              <div className="rpi-report-comm-intro">
                <p>占有欲相关的冲突往往源于沟通不良。学习以下技巧，可以帮助您更好地表达需求、理解伴侣：</p>
              </div>

              <div className="rpi-report-comm-techniques">
                <div className="rpi-report-comm-item">
                  <div className="rpi-report-comm-number">1</div>
                  <div className="rpi-report-comm-content">
                    <h4>"我"式表达</h4>
                    <div className="rpi-report-comm-example">
                      <div className="rpi-report-comm-bad">
                        <span className="rpi-report-comm-label bad">❌ 指责式：</span>
                        "你总是不理我，你根本不在乎我！"
                      </div>
                      <div className="rpi-report-comm-good">
                        <span className="rpi-report-comm-label good">✅ "我"式：</span>
                        "当你没有及时回我消息时，我感到被忽视和不安，我需要感受到被重视。"
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rpi-report-comm-item">
                  <div className="rpi-report-comm-number">2</div>
                  <div className="rpi-report-comm-content">
                    <h4>积极倾听</h4>
                    <ul className="rpi-report-comm-tips">
                      <li>放下手机，给予全部注意力</li>
                      <li>不打断，让对方说完</li>
                      <li>复述确认："你是说...对吗？"</li>
                      <li>理解感受："我理解你的感受"</li>
                    </ul>
                  </div>
                </div>

                <div className="rpi-report-comm-item">
                  <div className="rpi-report-comm-number">3</div>
                  <div className="rpi-report-comm-content">
                    <h4>暂停技巧</h4>
                    <p>当情绪激动时：</p>
                    <ul className="rpi-report-comm-tips">
                      <li>"我现在很生气，需要冷静一下，我们20分钟后再谈好吗？"</li>
                      <li>避免在愤怒时做决定或说狠话</li>
                      <li>冷静后主动重启对话</li>
                    </ul>
                  </div>
                </div>

                <div className="rpi-report-comm-item">
                  <div className="rpi-report-comm-number">4</div>
                  <div className="rpi-report-comm-content">
                    <h4>定期关系对话</h4>
                    <p>每周安排固定时间（如周日晚上）进行关系对话：</p>
                    <ul className="rpi-report-comm-tips">
                      <li>分享本周的感受和需求</li>
                      <li>表达欣赏和感谢</li>
                      <li>讨论待解决的问题</li>
                      <li>制定下周改善计划</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 30天自我成长计划 */}
          <div className="rpi-report-section">
            <h2 className="rpi-report-section-title">
              <span className="rpi-report-section-icon">📅</span>
              30天自我成长计划
            </h2>
            <div className="rpi-report-growth-card">
              <p className="rpi-report-growth-intro">
                改变需要时间和坚持。以下是一个为期30天的系统化成长计划，帮助您逐步降低不健康的占有欲，建立更和谐的关系：
              </p>

              <div className="rpi-report-growth-timeline">
                <div className="rpi-report-growth-phase">
                  <div className="rpi-report-growth-phase-header">
                    <span className="rpi-report-growth-week">第1周</span>
                    <span className="rpi-report-growth-theme">自我觉察期</span>
                  </div>
                  <ul className="rpi-report-growth-tasks">
                    <li>每天记录情绪日记，识别占有欲触发点</li>
                    <li>完成"我的不安全感来源"书面反思</li>
                    <li>练习正念冥想，每天10分钟</li>
                    <li>与伴侣坦诚分享内心感受（选择合适时机）</li>
                  </ul>
                </div>

                <div className="rpi-report-growth-phase">
                  <div className="rpi-report-growth-phase-header">
                    <span className="rpi-report-growth-week">第2周</span>
                    <span className="rpi-report-growth-theme">认知调整期</span>
                  </div>
                  <ul className="rpi-report-growth-tasks">
                    <li>学习并实践认知重构技巧</li>
                    <li>每当嫉妒或焦虑出现，写下并挑战非理性想法</li>
                    <li>阅读《依恋理论》或相关书籍</li>
                    <li>减少查看伴侣手机/社交媒体的频率</li>
                  </ul>
                </div>

                <div className="rpi-report-growth-phase">
                  <div className="rpi-report-growth-phase-header">
                    <span className="rpi-report-growth-week">第3周</span>
                    <span className="rpi-report-growth-theme">行为改变期</span>
                  </div>
                  <ul className="rpi-report-growth-tasks">
                    <li>培养独立兴趣爱好，每周2-3次</li>
                    <li>给伴侣更多个人空间，减少控制行为</li>
                    <li>练习"我"式沟通，表达需求而非指责</li>
                    <li>与朋友社交，拓展社交圈</li>
                  </ul>
                </div>

                <div className="rpi-report-growth-phase">
                  <div className="rpi-report-growth-phase-header">
                    <span className="rpi-report-growth-week">第4周</span>
                    <span className="rpi-report-growth-theme">巩固提升期</span>
                  </div>
                  <ul className="rpi-report-growth-tasks">
                    <li>回顾30天的成长变化，写总结</li>
                    <li>与伴侣进行关系对话，听取反馈</li>
                    <li>制定长期成长计划</li>
                    <li>如有需要，和朋友或伴侣多交流</li>
                  </ul>
                </div>
              </div>

              <div className="rpi-report-growth-tips">
                <h4>💡 坚持小贴士</h4>
                <ul>
                  <li>设置每日提醒，养成习惯</li>
                  <li>找一个支持者（朋友/伴侣）互相督促</li>
                  <li>每周回顾进展，记录小成功</li>
                  <li>对自己保持耐心，改变需要时间</li>
                  <li>偶尔退步是正常的，重要的是继续前进</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 学习资源 */}
          <div className="rpi-report-section">
            <h2 className="rpi-report-section-title">
              <span className="rpi-report-section-icon">📚</span>
              推荐学习资源
            </h2>
            <div className="rpi-report-resources-grid">
              {/* 推荐书籍 */}
              <div className="rpi-report-resource-column">
                <div className="rpi-report-resource-column-header">
                  <span className="rpi-report-resource-column-icon">📚</span>
                  <span>推荐书籍</span>
                </div>
                <ul className="rpi-report-resource-list">
                  <li>《依恋理论》- Bowlby</li>
                  <li>《亲密关系》- Rowland Miller</li>
                  <li>《爱的五种语言》- Gary Chapman</li>
                </ul>
              </div>

              {/* 在线课程 */}
              <div className="rpi-report-resource-column">
                <div className="rpi-report-resource-column-header">
                  <span className="rpi-report-resource-column-icon">🎓</span>
                  <span>在线课程</span>
                </div>
                <ul className="rpi-report-resource-list">
                  <li>亲密关系心理学</li>
                  <li>情绪管理与沟通技巧</li>
                  <li>依恋类型与关系模式</li>
                </ul>
              </div>

              {/* 专业支持 */}
              <div className="rpi-report-resource-column">
                <div className="rpi-report-resource-column-header">
                  <span className="rpi-report-resource-column-icon">💬</span>
                  <span>专业支持</span>
                </div>
                <ul className="rpi-report-resource-list">
                  <li>情感交流社群</li>
                  <li>恋爱话题播客</li>
                  <li>心理学科普文章</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 评估信息 */}
          <div className="rpi-report-info-card">
            <h3>评估信息</h3>
            <div className="rpi-report-info-grid">
              <div className="rpi-report-info-item">
                <span className="rpi-report-info-label">评估类型:</span>
                <span className="rpi-report-info-value">{type === 'self' ? '给自己测' : '为恋人测'}</span>
              </div>
              <div className="rpi-report-info-item">
                <span className="rpi-report-info-label">完成时间:</span>
                <span className="rpi-report-info-value">{reportData.timestamp}</span>
              </div>
              <div className="rpi-report-info-item">
                <span className="rpi-report-info-label">回答题数:</span>
                <span className="rpi-report-info-value">40 题</span>
              </div>
              <div className="rpi-report-info-item">
                <span className="rpi-report-info-label">会话ID:</span>
                <span className="rpi-report-info-value">session_{localStorage.getItem('sessionId')}</span>
              </div>
            </div>
          </div>

          {/* 重要声明 */}
          <div className="rpi-report-disclaimer-card">
            <span className="rpi-report-disclaimer-icon">⚠️</span>
            <div>
              <strong>重要声明</strong>
              <p>
                本测评结果仅供娱乐参考，不构成专业诊断。RPI指数是一个有趣的自我探索工具，
                帮助你了解自己在恋爱关系中的小特点。轻松看待结果，开心就好！
              </p>
              <p>
                您的所有数据都安全地保存在本地设备上，我们不会收集或传输你的个人信息。
              </p>
            </div>
          </div>

          {/* 底部操作按钮 */}
          <div className="rpi-report-footer-actions">
            <Button 
              size="large"
              icon={<ShareAltOutlined />}
              className="rpi-report-footer-btn-share"
              onClick={() => setShareModalVisible(true)}
            >
              分享结果
            </Button>
            <Button 
              size="large"
              icon={<HomeOutlined />}
              onClick={handleBackToHome}
              className="rpi-report-footer-btn"
            >
              返回主页
            </Button>
            <Button 
              type="primary"
              size="large"
              icon={<ReloadOutlined />}
              onClick={handleRestart}
              className="rpi-report-footer-btn-primary"
            >
              重新测试
            </Button>
          </div>
        </div>
      </Content>

      {/* 分享弹窗 */}
      <Modal
        open={shareModalVisible}
        onCancel={() => setShareModalVisible(false)}
        footer={null}
        width={560}
        centered
        className="rpi-share-modal"
      >
        <div className="rpi-share-modal-content">
          {/* 分享卡片预览 */}
          <div className="rpi-share-preview">
            <div className="rpi-share-preview-icon">
              💕
            </div>
            <h3 className="rpi-share-preview-title">恋爱占有欲指数 (RPI)</h3>
            <p className="rpi-share-preview-subtitle">Romantic Possessiveness Index</p>
            <div className="rpi-share-preview-score">{rpiIndex}</div>
            <div className="rpi-share-preview-level" style={{ 
              background: levelColor,
              color: 'white'
            }}>
              {levelText}
            </div>
            <div className="rpi-share-preview-progress">
              <Progress 
                percent={(rpiIndex / 100) * 100} 
                showInfo={false}
                strokeColor={{
                  '0%': '#E92063',
                  '100%': '#D81B5B',
                }}
                strokeWidth={6}
              />
              <div className="rpi-share-preview-labels">
                <span>0 （自由自在）</span>
                <span>50 （恰到好处）</span>
                <span>100 （爱到极致）</span>
              </div>
            </div>
            <p className="rpi-share-preview-footer">基于科学心理测量量表的专业评估工具</p>
          </div>

          {/* 分享标题 */}
          <h3 className="rpi-share-section-title">
            <ShareAltOutlined style={{ color: '#E92063' }} />
            分享我的RPI评估结果
          </h3>
          <p className="rpi-share-section-desc">选择您喜欢的方式分享评估结果，所有数据都经过加密处理</p>

          {/* 社交媒体分享 */}
          <div className="rpi-share-section">
            <h4 className="rpi-share-subtitle">📱 社交媒体分享</h4>
            <div className="rpi-share-social-grid">
              <div className="rpi-share-social-item" onClick={() => handleSocialShare('weibo')}>
                <div className="rpi-share-social-icon" style={{ background: 'linear-gradient(135deg, #FF8200 0%, #FF6000 100%)' }}>
                  <WeiboOutlined />
                </div>
                <span>微博</span>
              </div>
              <div className="rpi-share-social-item" onClick={() => handleSocialShare('wechat')}>
                <div className="rpi-share-social-icon" style={{ background: 'linear-gradient(135deg, #09BB07 0%, #07C160 100%)' }}>
                  <WechatOutlined />
                </div>
                <span>微信</span>
              </div>
              <div className="rpi-share-social-item" onClick={() => handleSocialShare('qq')}>
                <div className="rpi-share-social-icon" style={{ background: 'linear-gradient(135deg, #56B4FE 0%, #3EADFF 100%)' }}>
                  <QqOutlined />
                </div>
                <span>QQ空间</span>
              </div>
              <div className="rpi-share-social-item" onClick={() => handleSocialShare('douban')}>
                <div className="rpi-share-social-icon" style={{ background: 'linear-gradient(135deg, #E92063 0%, #D81B5B 100%)' }}>
                  <HeartOutlined />
                </div>
                <span>豆瓣</span>
              </div>
            </div>
          </div>

          {/* 保存为图片 */}
          <div className="rpi-share-section">
            <h4 className="rpi-share-subtitle">🖼️ 保存为图片</h4>
            <p style={{ fontSize: 12, color: '#999', marginBottom: 12 }}>
              保存您的恋爱占有欲指数报告图片
            </p>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={handleSaveAsImage}
              size="large"
              block
              style={{ 
                background: 'linear-gradient(135deg, #E92063 0%, #FF6B9D 100%)',
                border: 'none',
                height: 44,
                fontSize: 15,
                fontWeight: 500
              }}
            >
              生成并保存图片
            </Button>
          </div>

          {/* 直接分享 */}
          <div className="rpi-share-section">
            <h4 className="rpi-share-subtitle">🔗 直接分享</h4>
            <div className="rpi-share-link-box">
              <Input 
                value={shareUrl} 
                readOnly 
                className="rpi-share-link-input"
              />
              <Button 
                type="primary" 
                icon={<CopyOutlined />}
                onClick={handleCopyLink}
                className="rpi-share-copy-btn"
              >
                复制链接
              </Button>
            </div>
          </div>

          {/* 预设分享文案 */}
          <div className="rpi-share-section">
            <h4 className="rpi-share-subtitle">💬 预设分享文案</h4>
            <div className="rpi-share-text-box">
              <div className="rpi-share-text-content">
                我的恋爱占有欲指数 (RPI) 是 {rpiIndex} 分，处于「{levelText}」水平！💕
                <br/><br/>
                RPI 恋爱占有欲指数测试 - 基于心理学研究的专业测评工具，帮助你更好地了解恋爱关系中的占有欲特征，促进健康和谐的亲密关系！
                <br/><br/>
                立即测试：https://xinlitest.vip/
                <br/><br/>
                #RPI测试 #恋爱占有欲 #关系心理学
              </div>
              <Button 
                icon={<CopyOutlined />}
                onClick={handleCopyText}
                className="rpi-share-text-copy-btn"
              >
                复制文案
              </Button>
            </div>
          </div>

          {/* 隐私提示 */}
          <div className="rpi-share-privacy">
            <div className="rpi-share-privacy-icon">🔒</div>
            <div className="rpi-share-privacy-content">
              <strong>隐私保护：</strong>分享的结果经过匿名化处理，不包含个人身份信息。分享链接仅包含结果摘要，他人无法访问您的详细答题记录。
            </div>
          </div>
        </div>
      </Modal>

      {/* 隐藏的分享图片组件 */}
      <div 
        ref={shareImageRef}
        style={{
          position: 'fixed',
          left: '-9999px',
          top: 0,
          width: '750px',
          background: '#F5F5F5',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
      >
        {/* 主卡片 */}
        <div style={{
          background: 'white',
          margin: '40px',
          borderRadius: 24,
          padding: '60px 50px',
          border: '3px solid #E92063'
        }}>
          {/* 顶部爱心和标题 */}
          <div style={{ textAlign: 'center', marginBottom: 50 }}>
            <div style={{ 
              width: 120, 
              height: 120, 
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(233, 32, 99, 0.1) 0%, rgba(255, 182, 193, 0.2) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 60
            }}>
              💕
            </div>
            <div style={{ 
              fontSize: 28, 
              fontWeight: 'bold', 
              color: '#E92063',
              letterSpacing: 1
            }}>
              恋人占有欲指数 (RPI)
            </div>
          </div>

          {/* RPI分数 */}
          <div style={{ textAlign: 'center', marginBottom: 30 }}>
            <div style={{ 
              fontSize: 120, 
              fontWeight: 'bold',
              color: '#E92063',
              lineHeight: 1,
              marginBottom: 20
            }}>
              {rpiIndex}
            </div>
            <div style={{
              display: 'inline-block',
              padding: '12px 40px',
              background: levelColor,
              color: 'white',
              borderRadius: 40,
              fontSize: 22,
              fontWeight: 'bold'
            }}>
              {levelText}
            </div>
          </div>

          {/* 描述文字 */}
          <div style={{
            fontSize: 16,
            color: '#333',
            textAlign: 'center',
            marginBottom: 40,
            lineHeight: 1.8
          }}>
            {type === 'self' ? '您' : 'Ta'}的恋爱占有欲指数为 <span style={{ color: '#E92063', fontWeight: 'bold' }}>{rpiIndex}</span>，处于「<span style={{ color: '#E92063', fontWeight: 'bold' }}>{levelText}</span>」水平
          </div>

          {/* 进度条 */}
          <div style={{ marginBottom: 50 }}>
            <div style={{
              height: 12,
              background: 'linear-gradient(to right, #52C41A 0%, #FAAD14 50%, #E92063 100%)',
              borderRadius: 6,
              position: 'relative',
              marginBottom: 15
            }}>
              <div style={{
                position: 'absolute',
                right: 0,
                top: -2,
                width: 16,
                height: 16,
                background: '#E92063',
                borderRadius: '50%',
                border: '3px solid white',
                boxShadow: '0 2px 8px rgba(233, 32, 99, 0.4)'
              }} />
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 13,
              color: '#999'
            }}>
              <span>0 （自由自在）</span>
              <span>50 （恰到好处）</span>
              <span>100 （爱到极致）</span>
            </div>
          </div>

          {/* 综合结果解释 */}
          <div style={{
            background: '#FFF5F8',
            borderRadius: 16,
            padding: '30px',
            border: '1px solid #FFE4E9'
          }}>
            <div style={{
              fontSize: 18,
              fontWeight: 'bold',
              color: '#333',
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <span style={{ fontSize: 20 }}>💭</span>
              综合结果解释
            </div>
            
            <div style={{
              fontSize: 15,
              color: '#333',
              lineHeight: 1.8,
              marginBottom: 15
            }}>
              您的恋爱占有欲指数为 <span style={{ fontWeight: 'bold', color: '#E92063' }}>{rpiIndex} 分</span>，处于「<span style={{ fontWeight: 'bold', color: '#E92063' }}>{levelText}</span>」水平。
            </div>
            
            {rpiIndex >= 80 && (
              <>
                <div style={{
                  fontSize: 14,
                  color: '#666',
                  lineHeight: 1.8,
                  marginBottom: 12
                }}>
                  您的占有欲非常强烈，可能表现为频繁查看伴侣动态、限制社交活动等。这种极端的占有欲通常与深层的不安全感有关。
                </div>
                
                <div style={{
                  fontSize: 14,
                  color: '#666',
                  lineHeight: 1.8,
                  marginBottom: 12
                }}>
                  在以下维度上得分极高：控制欲望、嫉妒倾向、情感依赖、关系不安。
                </div>
                
                <div style={{
                  fontSize: 14,
                  color: '#666',
                  lineHeight: 1.8
                }}>
                  这种程度的占有欲可能已经严重影响了关系质量。建议试着给彼此更多空间，培养独立的兴趣爱好。如果感到情绪难以控制，可以向信任的朋友倾诉。
                </div>
              </>
            )}
            
            {rpiIndex >= 60 && rpiIndex < 80 && (
              <>
                <div style={{
                  fontSize: 14,
                  color: '#666',
                  lineHeight: 1.8,
                  marginBottom: 12
                }}>
                  您的占有欲较强烈，可能在某些情况下会希望更多地了解伴侣的行踪和社交情况。
                </div>
                
                <div style={{
                  fontSize: 14,
                  color: '#666',
                  lineHeight: 1.8
                }}>
                  在以下维度上得分较高：嫉妒倾向、情感依赖、关系不安。这些特点可能影响你在恋爱关系中的行为方式和情感体验。
                </div>
              </>
            )}
            
            {rpiIndex >= 40 && rpiIndex < 60 && (
              <div style={{
                fontSize: 14,
                color: '#666',
                lineHeight: 1.8
              }}>
                您的占有欲处于中等水平，在关系中能够较好地平衡亲密与独立。既能表达对伴侣的在意，又能给予彼此适当的个人空间。
              </div>
            )}
            
            {rpiIndex < 40 && (
              <div style={{
                fontSize: 14,
                color: '#666',
                lineHeight: 1.8
              }}>
                您的占有欲较低，倾向于给予伴侣更多的自由空间和信任。您注重个人独立性，也尊重伴侣的个人空间和社交自由。
              </div>
            )}
          </div>
        </div>

        {/* 底部链接 */}
        <div style={{
          textAlign: 'center',
          fontSize: 15,
          color: '#999',
          paddingBottom: 30,
          fontWeight: 500
        }}>
          https://xinlitest.vip/
        </div>
      </div>
    </Layout>
  );
};

export default ReportPage;

